/**
 * Learning Catalog: what Arta learned, toggled between two views on one screen: Conversations (the learning history: every
 * case Arta learned from, with the per-cause track record; a case can be undone and redone) and Manual (Learning editor
 * patterns and in-chat teaches, learned as soon as they are saved; searchable, undoable and learnable again; a learned pattern is not edited).
 * Both are compact tables of one server page each (newest first); a row opens its details and actions in a popup. Only the
 * shown view loads; each keeps its filters and its loaded page while the other is shown. The dropdown choices are loaded once
 * for the page and shared by both views, and so is the cache of how each opened conversation started (the popups' first
 * query and context). Route: /learning/catalog.
 */
import React, { useCallback, useState } from "react";
import { Loader2, RefreshCw, WifiOff } from "lucide-react";

import AdminSideBar from "../../AdminSideBar";
import UserProfileMenu from "../../UserProfileMenu";
import { getStoredRole } from "../../../utils/auth";
import LearningAgentSideBar from "../LearningAgentSideBar";
import CatalogDetail from "./CatalogDetail";
import CatalogFilters, { BTN_PRIMARY, ViewToggle } from "./CatalogFilters";
import CatalogPager from "./CatalogPager";
import CatalogTable from "./CatalogTable";
import DetailModal from "./DetailModal";
import HistoryView from "./HistoryView";
import useCatalog from "./useCatalog";
import useCatalogOptions from "./useCatalogOptions";
import useHistory from "./useHistory";
import { SessionContextCache } from "./useSessionContext";
import { STATUS } from "./usePagedList";
import { CATALOG_TEXT, CATALOG_VIEW, DEFAULT_VIEW, PAGE_SIZE } from "./catalogText";

const CatalogPage = ({ onLogout }) => {
  const isAdmin = getStoredRole() === "admin";
  const [view, setView] = useState(DEFAULT_VIEW);
  const manualView = view === CATALOG_VIEW.MANUAL;
  const catalog = useCatalog({ enabled: manualView });
  const history = useHistory({ enabled: !manualView });
  const { status, items, total, offset, setOffset, filters, setFilter, clearFilters, refresh, error } = catalog;
  const { products, historyOptions, refreshProducts } = useCatalogOptions({ manualState: filters.state, historyShown: !manualView });
  const current = manualView ? catalog : history;
  const [detail, setDetail] = useState(null);
  const [notice, setNotice] = useState("");
  const [sessionContexts] = useState(() => new Map()); // how each opened conversation started, kept for the page's lifetime

  // A write changes the counts and may change the other view: the hidden one reloads when it is next shown.
  const changed = (text) => { setNotice(text); refresh(); refreshProducts(); history.refresh(); };
  const historyChanged = () => { catalog.refresh(); refreshProducts(); };
  const onRefresh = () => { current.refresh(); if (manualView) refreshProducts(); };
  const onFilter = (key, value) => { setNotice(""); setFilter(key, value); };
  const onView = (value) => { setNotice(""); setView(value); };
  const openDetail = useCallback((item) => { setNotice(""); setDetail(item); }, []);

  const detailChanged = (text) => { setDetail(null); changed(text); };

  return (
    <SessionContextCache.Provider value={sessionContexts}>
      <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
        {isAdmin ? <AdminSideBar onLogout={onLogout} /> : <LearningAgentSideBar onLogout={onLogout} />}
        <div className="relative flex h-full min-w-0 flex-1 flex-col overflow-hidden">
          {/* Top bar: the same bar as Batch Diagnostics (white, bottom border, small title, cyan actions, avatar at the right) */}
          <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-200 bg-white py-4 pl-6 pr-16">
            <div>
              <h1 className="text-base font-semibold text-[#253439]">{CATALOG_TEXT.title}</h1>
              <p className="mt-0.5 text-xs text-gray-400">{CATALOG_TEXT.description[view]}</p>
            </div>
            <button type="button" onClick={onRefresh} disabled={current.status === STATUS.LOADING}
              className="flex items-center gap-1.5 rounded-lg bg-[#06b6d4] px-3 py-1.5 text-sm font-medium text-white transition hover:bg-cyan-600 disabled:bg-gray-300 disabled:cursor-not-allowed">
              <RefreshCw size={14} className={current.status === STATUS.LOADING ? "animate-spin" : ""} />{CATALOG_TEXT.refresh}
            </button>
          </div>
          <div className="absolute right-4 top-4 z-50"><UserProfileMenu onLogout={onLogout} /></div>
          <div className="flex min-w-0 flex-1 flex-col overflow-y-auto overflow-x-hidden px-6 py-4">
            <div className="mb-4"><ViewToggle value={view} onChange={onView} /></div>
            {manualView ? (
              <>
                <CatalogFilters filters={filters} products={products} onChange={onFilter} onClear={() => { setNotice(""); clearFilters(); }} />

                {notice && <p className="mb-3 text-sm text-emerald-700" role="status">{notice}</p>}
                {status === STATUS.LOADING && items.length === 0 && (
                  <p className="flex items-center gap-2 text-sm text-gray-500" role="status"><Loader2 size={14} className="animate-spin" />{CATALOG_TEXT.loading}</p>
                )}
                {status === STATUS.NOT_CONNECTED && (
                  <p className="flex items-center gap-2 text-sm text-gray-500" role="status"><WifiOff size={14} />{CATALOG_TEXT.notConnected}</p>
                )}
                {status === STATUS.FAILED && <p className="text-sm text-red-600" role="alert">{error || CATALOG_TEXT.failed}</p>}
                {status === STATUS.READY && items.length === 0 && <p className="text-sm text-gray-500">{CATALOG_TEXT.empty}</p>}
                {items.length > 0 && status !== STATUS.FAILED && (
                  <>
                    <CatalogTable items={items} loading={status === STATUS.LOADING} onOpen={openDetail} />
                    <CatalogPager offset={offset} limit={PAGE_SIZE} total={total} onOffset={setOffset} />
                  </>
                )}
              </>
            ) : <HistoryView history={history} options={historyOptions} products={products} onChanged={historyChanged} />}
          </div>
        </div>
        {detail && (
          <DetailModal title={CATALOG_TEXT.detail.title} onClose={() => setDetail(null)}>
            <CatalogDetail item={detail} onChanged={detailChanged} />
          </DetailModal>
        )}
      </div>
    </SessionContextCache.Provider>
  );
};

export default CatalogPage;
